import './sponsorship-card.scss';

import type { TFunction } from 'i18next';
import type { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { BadgeProps } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import {
  ItemCard,
  ItemCardBody,
  ItemCardMeta,
  ItemCardSection,
} from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { LinkCard } from '@sola/ark-core/molecules/link-card/link-card.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { getAgeFromBirthday } from '@sola/shared-utils/children/utils.ts';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.tsx';

const getBadgeProps = (
  commitmentStatus: string,
  t: TFunction,
): BadgeProps | undefined => {
  switch (commitmentStatus) {
    case 'Closed': {
      return {
        iconBefore: 'alert-triangle',
        label: t('my:sponsorshipRelationship.prior'),
        color: 'warning',
      };
    }
    // TODO: Departing, Pending
  }
};

const getCenterId = (commitment: GraphQLCommitment) =>
  commitment.participant?.localId?.slice(0, 6);

const useSponsorshipCardData = (
  commitment: GraphQLCommitment,
  showCenter: boolean,
) => {
  const { t } = useTranslation(['children', 'my']);
  const locationIconName: IconNames = showCenter ? 'church' : 'globe-05';
  const { participant } = commitment;
  if (!participant) {
    return { locationIconName };
  }

  const { birthdate, country, name, names } = participant;
  const centerId = getCenterId(commitment);
  const birthdayText = birthdate
    ? t('children:sac.age', {
        count: Number(getAgeFromBirthday(birthdate)),
        birthday: birthdate,
      })
    : undefined;

  const badgeProps = getBadgeProps(commitment.status, t);
  const centerText = centerId
    ? t('my:childDetails.fcpID', { centerId })
    : undefined;

  return {
    name: names?.preferred ?? name,
    locationIconName,
    locationText: showCenter ? centerText : country,
    birthdayText,
    badgeProps,
  };
};

export type SponsorshipCardProps = {
  commitment: GraphQLCommitment;
  action?: ReactNode;
  badge?: ReactNode;
  className?: string;
  showCenter?: boolean;
};

const SponsorshipCardDetails: React.FC<{
  badge?: ReactNode;
  commitment: GraphQLCommitment;
  showCenter?: boolean;
}> = ({ badge, commitment, showCenter = false }) => {
  const { name, locationIconName, locationText, birthdayText } =
    useSponsorshipCardData(commitment, showCenter);

  return (
    <div className="SponsorshipCard__content flex flex-col gap-snug-md">
      {badge && <div className="SponsorshipCard__badge">{badge}</div>}
      <div className="SponsorshipCard__title heading-xs md:heading-sm">
        {name}
      </div>

      {birthdayText && (
        <div className="SponsorshipCard__meta flex items-center gap-comfy-xs md:gap-comfy-sm">
          <ArkIcon
            iconName="birthday-cake"
            size="1.5rem"
            className="shrink-0"
          />
          <span className="body-sm md:body-md">{birthdayText}</span>
        </div>
      )}

      {locationText && (
        <div className="SponsorshipCard__meta flex items-center gap-comfy-xs md:gap-comfy-sm">
          <ArkIcon
            iconName={locationIconName}
            size="1.5rem"
            className="shrink-0"
          />
          <span className="body-sm md:body-md">{locationText}</span>
        </div>
      )}
    </div>
  );
};

export const SponsorshipCardWrapper: React.FC<
  PropsWithChildren<{ className: SponsorshipCardProps['className'] }>
> = ({ className, children }) => {
  return (
    <ItemCard className={cn('SponsorshipCard relative', className)}>
      <ItemCardSection>
        <ItemCardBody className="SponsorshipCard__body">
          {children}
        </ItemCardBody>
      </ItemCardSection>
    </ItemCard>
  );
};
export const SponsorshipCard: React.FC<SponsorshipCardProps> = ({
  action,
  badge,
  className,
  commitment,
  showCenter = false,
}) => {
  if (!commitment.participant) {
    return null;
  }

  return (
    <SponsorshipCardWrapper className={className}>
      <CommitmentAvatar commitment={commitment} size="xl" />
      <SponsorshipCardDetails
        badge={badge}
        commitment={commitment}
        showCenter={showCenter}
      />
      {action && (
        <ItemCardMeta className="SponsorshipCard__action">
          {action}
        </ItemCardMeta>
      )}
    </SponsorshipCardWrapper>
  );
};

export type SponsorshipLinkCardProps = {
  commitment: GraphQLCommitment;
  href: string;
  onClick?: () => void;
};

export const SponsorshipLinkCard: React.FC<SponsorshipLinkCardProps> = ({
  commitment,
  href,
  onClick,
}) => {
  const { badgeProps } = useSponsorshipCardData(commitment, false);

  return (
    <LinkCard
      className="SponsorshipCard"
      href={href}
      onClick={onClick}
      graphic={<CommitmentAvatar commitment={commitment} size="xl" />}
    >
      <SponsorshipCardDetails
        badge={badgeProps && <Badge responsive {...badgeProps} />}
        commitment={commitment}
      />
    </LinkCard>
  );
};
